import React from "react";

export default function Privacy() {
  return (
    <main style={styles.main}>
      <header style={styles.header}>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.meta}>Last updated: 1/20/2026</p>
      </header>

      <section style={styles.section}>
        <p>
          DAD PARTNERS ("we," "our," or "us") is committed to protecting your
          privacy and being transparent about how we collect, use, and protect
          your personal information. This Privacy Policy explains our data
          practices in clear, understandable terms.
        </p>

        <p style={styles.callout}>
          <strong>Quick Summary:</strong> We collect only the information
          necessary to provide our services, never sell your personal data, and
          give you full control over your information. You have the right to
          access, correct, or delete your data at any time.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>1. Data Controller Information</h2>
        <p>DAD PARTNERS is the data controller responsible for your personal information. You can contact us at:</p>
        <address style={styles.address}>
          <div><strong>DAD PARTNERS</strong></div>
          <div>124 Chapel Fields Ln</div>
          <div>Garner, NC 27529</div>
          <div>USA</div>
          <div>Email: <a href="mailto:DarbyEntLLC@gmail.com">DarbyEntLLC@gmail.com</a></div>
          <div>Phone: <a href="tel:+19199371711">(919) 937-1711</a></div>
        </address>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>2. Information We Collect</h2>

        <h3 style={styles.h3}>Information You Provide Directly</h3>
        <ul style={styles.ul}>
          <li><strong>Account Information:</strong> Name, email address, password, and profile details</li>
          <li><strong>Communication Data:</strong> Messages you send us, support requests, and feedback</li>
          <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely by our payment processors)</li>
          <li><strong>Service Data:</strong> Content you create, upload, or share through our services</li>
        </ul>

        <h3 style={styles.h3}>Information We Collect Automatically</h3>
        <ul style={styles.ul}>
          <li><strong>Usage Data:</strong> How you interact with our services, features used, and time spent</li>
          <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
          <li><strong>Analytics Data:</strong> Performance metrics, error logs, and usage patterns</li>
          <li><strong>Location Data:</strong> General geographic location based on IP address</li>
        </ul>

        <h3 style={styles.h3}>Information from Third Parties</h3>
        <p>
          We may receive information from business partners, social media platforms
          (if you connect your accounts), and public databases, but only when
          necessary for providing our services.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>3. How We Use Your Information</h2>
        <p>We process your personal information for the following purposes:</p>

        <h3 style={styles.h3}>Service Provision (Legal Basis: Contract Performance)</h3>
        <ul style={styles.ul}>
          <li>Creating and managing your account</li>
          <li>Providing our services and features</li>
          <li>Processing payments and transactions</li>
          <li>Providing customer support</li>
        </ul>

        <h3 style={styles.h3}>Service Improvement (Legal Basis: Legitimate Interest)</h3>
        <ul style={styles.ul}>
          <li>Analyzing usage patterns to improve functionality</li>
          <li>Developing new features and services</li>
          <li>Conducting research and analytics</li>
          <li>Testing and optimizing our platform</li>
        </ul>

        <h3 style={styles.h3}>Communication (Legal Basis: Consent or Legitimate Interest)</h3>
        <ul style={styles.ul}>
          <li>Sending service updates and important notices</li>
          <li>Responding to your inquiries and support requests</li>
          <li>Marketing communications (only with your consent)</li>
          <li>Security alerts and fraud prevention</li>
        </ul>

        <h3 style={styles.h3}>Legal Compliance (Legal Basis: Legal Obligation)</h3>
        <ul style={styles.ul}>
          <li>Complying with applicable laws and regulations</li>
          <li>Responding to legal processes and law enforcement requests</li>
          <li>Protecting our rights and preventing fraud</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>4. Information Sharing and Disclosure</h2>
        <p><strong>We never sell your personal information.</strong> We only share your information in the following limited circumstances:</p>

        <h3 style={styles.h3}>Service Providers</h3>
        <p>
          We work with trusted third-party companies that help us operate our services,
          such as cloud hosting providers, payment processors, and analytics services.
          These providers are contractually bound to protect your information and use
          it only for the services they provide to us.
        </p>

        <h3 style={styles.h3}>Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, or sale of assets, your information
          may be transferred as part of that transaction. We will notify you before your
          information is transferred and becomes subject to a different privacy policy.
        </p>

        <h3 style={styles.h3}>Legal Requirements</h3>
        <p>
          We may disclose your information if required by law, legal process, or government
          request, or if we believe disclosure is necessary to protect our rights, your
          safety, or the safety of others.
        </p>

        <h3 style={styles.h3}>Consent</h3>
        <p>
          We may share your information with your explicit consent for specific purposes
          not covered in this policy.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>5. Data Security</h2>
        <p>We implement industry-standard security measures to protect your personal information:</p>
        <ul style={styles.ul}>
          <li><strong>Encryption:</strong> Data is encrypted in transit and at rest using advanced encryption standards</li>
          <li><strong>Access Controls:</strong> Strict access controls ensure only authorized personnel can access your data</li>
          <li><strong>Regular Audits:</strong> We regularly review and update our security practices</li>
          <li><strong>Incident Response:</strong> We have procedures in place to detect and respond to security incidents</li>
          <li><strong>Employee Training:</strong> Our team is trained on data protection and security best practices</li>
        </ul>
        <p>
          While we take extensive precautions, no method of transmission over the internet is 100% secure.
          We cannot guarantee absolute security but are committed to protecting your information using the
          most current security standards.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>6. Data Retention</h2>
        <p>We retain your personal information only as long as necessary for the purposes outlined in this policy:</p>
        <ul style={styles.ul}>
          <li><strong>Account Data:</strong> For the duration of your account plus 90 days after deletion</li>
          <li><strong>Transaction Records:</strong> 7 years for tax and legal compliance</li>
          <li><strong>Support Communications:</strong> 3 years for service improvement</li>
          <li><strong>Analytics Data:</strong> Anonymized data may be retained indefinitely for research</li>
          <li><strong>Marketing Data:</strong> Until you withdraw consent or 2 years of inactivity</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>7. Your Privacy Rights</h2>
        <p>You have significant control over your personal information. Depending on your location, you may have the following rights:</p>

        <h3 style={styles.h3}>Universal Rights</h3>
        <ul style={styles.ul}>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Update or correct inaccurate personal information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Portability:</strong> Receive your data in a portable format</li>
          <li><strong>Withdrawal of Consent:</strong> Withdraw consent for processing at any time</li>
        </ul>

        <h3 style={styles.h3}>EU/UK Residents (GDPR)</h3>
        <ul style={styles.ul}>
          <li><strong>Restriction:</strong> Limit how we process your information</li>
          <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
          <li><strong>Automated Decision-Making:</strong> Opt out of automated profiling</li>
          <li><strong>Complaint:</strong> File a complaint with your local data protection authority</li>
        </ul>

        <h3 style={styles.h3}>California Residents (CCPA/CPRA)</h3>
        <ul style={styles.ul}>
          <li><strong>Know:</strong> Detailed information about data collection and sharing</li>
          <li><strong>Delete:</strong> Request deletion of personal information</li>
          <li><strong>Opt-Out:</strong> Opt out of the "sale" or "sharing" of personal information</li>
          <li><strong>Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
          <li><strong>Sensitive Data:</strong> Limit use of sensitive personal information</li>
        </ul>

        <p>
          To exercise your rights: Email us at{" "}
          <a href="mailto:DarbyEntLLC@gmail.com">DarbyEntLLC@gmail.com</a>{" "}
          or use our privacy request form. We will respond within 30 days and may need
          to verify your identity for security purposes.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>8. International Data Transfers</h2>
        <p>
          We may transfer your information to countries outside your country of residence. When we do:
        </p>
        <ul style={styles.ul}>
          <li>We ensure appropriate safeguards are in place (such as EU Standard Contractual Clauses)</li>
          <li>We work only with partners who provide adequate protection for your data</li>
          <li>We implement additional security measures for international transfers</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>9. Cookies and Tracking Technologies</h2>
        <p>
          We use cookies and similar technologies to improve your experience. See our Cookie Policy for
          detailed information about the cookies we use and how to manage your preferences.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>10. Children&apos;s Privacy</h2>
        <p>
          Our services are not intended for children under 13 years of age. We do not knowingly collect
          personal information from children under 13. If you believe we have collected information from
          a child under 13, please contact us immediately so we can delete it.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>11. Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will:</p>
        <ul style={styles.ul}>
          <li>Notify you of material changes via email or prominent notice on our website</li>
          <li>Post the updated policy with the new effective date</li>
          <li>Maintain previous versions for your reference</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>12. California &quot;Do Not Sell&quot; Notice</h2>
        <p>
          We do not sell personal information as defined by California law. We do not and will not sell
          your personal information to third parties.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>13. Contact Information</h2>
        <p>For privacy-related questions, to exercise your rights, or to file a complaint:</p>
        <ul style={styles.ul}>
          <li>Email: <a href="mailto:DarbyEntLLC@gmail.com">DarbyEntLLC@gmail.com</a></li>
          <li>
            Mail:
            <div style={{ marginTop: 8 }}>
              <strong>DAD PARTNERS</strong><br />
              Attention: Privacy Officer<br />
              124 Chapel Fields Ln<br />
              Garner, NC 27529<br />
              USA
            </div>
          </li>
          <li><strong>Response Time:</strong> We respond to privacy requests within 30 days.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>14. Regulatory Information</h2>
        <ul style={styles.ul}>
          <li><strong>EU/UK Residents:</strong> You can file complaints with your local data protection authority.</li>
          <li><strong>California Residents:</strong> You can file complaints with the California Privacy Protection Agency.</li>
          <li><strong>Industry-Specific Note:</strong> [Add any industry-specific privacy requirements here, such as HIPAA for healthcare, FERPA for education, etc.]</li>
        </ul>
        <p>
          This Privacy Policy is designed to be comprehensive and compliant with major privacy regulations worldwide.
          For questions about specific legal requirements in your jurisdiction, please contact us.
        </p>
      </section>
    </main>
  );
}

const styles = {
  main: {
    maxWidth: 980,
    margin: "0 auto",
    padding: "32px 20px",
    lineHeight: 1.65,
  },
  header: {
    marginBottom: 18,
  },
  h1: {
    margin: "0 0 6px 0",
    fontSize: 36,
    letterSpacing: "-0.02em",
  },
  meta: {
    margin: 0,
    opacity: 0.75,
  },
  section: {
    marginTop: 22,
  },
  h2: {
    margin: "18px 0 8px",
    fontSize: 22,
    letterSpacing: "-0.01em",
  },
  h3: {
    margin: "14px 0 6px",
    fontSize: 16,
    textTransform: "none",
  },
  ul: {
    marginTop: 8,
    marginBottom: 0,
    paddingLeft: 22,
  },
  address: {
    marginTop: 10,
    padding: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 12,
    fontStyle: "normal",
  },
  callout: {
    marginTop: 12,
    padding: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 12,
    background: "rgba(0,0,0,0.03)",
  },
};
